/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import {
  AccountsService,
  CouponsService,
  NewsService,
  WorkflowsService,
  type ListAccountEventsRequestParams,
  type ListCouponEventsRequestParams,
  type ListNewsEventsRequestParams,
  type ListWorkflowEventsRequestParams,
} from "@api/event";
import { withLoading } from "../features/loading.feature";
import { filter, pipe, switchMap, tap } from "rxjs";
import { setAllEntities, withEntities } from "@ngrx/signals/entities";
import { tapResponse } from "@ngrx/operators";
import { StorageKeys, StoreCollection } from "src/app/enums";
import { CouponEventDto } from "src/app/services/api/event/model/coupon-event-dto";
import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { PaginationStoreState, withPagination } from "../features/pagination.feature";
import { NewsEventDto } from "src/app/services/api/event/model/news-event-dto";
import {
  couponEventsConfig,
  initialState,
  postEventsConfig,
  customerEventsConfig,
  workflowEventsConfig,
  CustomerEventDto,
} from "./utils/initial-state";
import { withDatesFilter } from "../features/dates-filter.feature";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import * as Sentry from "@sentry/angular";
import { FromRouter, FromCustomer, RootState } from "..";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationComponent } from "src/app/modals/confirmation/confirmation.component";
import { DEFAULT_PAGINATION_SIZE } from "src/app/constants";
import { WorkflowEventDto } from "src/app/services/api/event/model/workflow-event-dto";
import { WorkflowEventDtoResponse } from "src/app/services/api/event/model/workflow-event-dto-response";

const paginationInitialState: PaginationStoreState<StoreCollection.Events> = {
  paginations: {
    [StoreCollection.Events]: { pageSize: DEFAULT_PAGINATION_SIZE, pageCount: 0, page: 1, total: 0 },
  },
};

export const EventsStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withLoading(),
  withPagination(paginationInitialState),
  withDatesFilter(),
  withEntities(couponEventsConfig),
  withEntities(postEventsConfig),
  withEntities(customerEventsConfig),
  withEntities(workflowEventsConfig),
  withProps(() => ({
    _eventsService: inject(CouponsService),
    _postEventsService: inject(NewsService),
    _customerEventsService: inject(AccountsService),
    _workflowEventsService: inject(WorkflowsService),
    _globalStore: inject(Store<RootState>),
    _matDialog: inject(MatDialog),
  })),
  withDevtools(StorageKeys.EventsStore),
  withComputed(store => ({
    // Every read below gates on this as well as on its own id. The client sends it as the `program` header and
    // throws when it is missing, and that throw happens inside the switchMap projection, so tapResponse never
    // sees it -- it would error the rxMethod subscription and leave the store dead for the rest of the page.
    programId: computed(() => store._globalStore.selectSignal(FromRouter.selectProgramId)()),
    couponId: computed(() => store._globalStore.selectSignal(FromRouter.selectCouponId)()),
    postId: computed(() => store._globalStore.selectSignal(FromRouter.selectPostId)()),
    workflowId: computed(() => store._globalStore.selectSignal(FromRouter.selectWorkflowId)()),
    accountId: computed(() => store._globalStore.selectSignal(FromRouter.selectQueryParams)()["accountId"] as string),
    userId: computed(() => store._globalStore.selectSignal(FromRouter.selectQueryParams)()["userId"] as string),
    membershipId: computed(() => {
      const accountId = store._globalStore.selectSignal(FromRouter.selectQueryParams)()["accountId"] as string;
      const userId = store._globalStore.selectSignal(FromRouter.selectQueryParams)()["userId"] as string;
      if (accountId && userId) {
        const membership = store._globalStore.selectSignal(FromCustomer.selectMembershipByUserId(userId, accountId))();
        return membership?.membershipId;
      }
      return undefined;
    }),
  })),
  withComputed(store => ({
    query: computed(() => ({
      programId: store.programId(),
      couponId: store.couponId(),
      postId: store.postId(),
      workflowId: store.workflowId(),
      userId: store.userId(),
      accountId: store.accountId(),
      membershipId: store.membershipId(),
      fromDate: store.fromDate(),
      toDate: store.toDate(),
      page: store.paginations[StoreCollection.Events].page(),
    })),
  })),
  withMethods(store => ({
    getCouponEvents: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        filter(query => !!query.programId && !!query.couponId),
        tap(() => store.setLoading(true)),
        switchMap(({ programId, couponId, fromDate, toDate, page }) => {
          const params: ListCouponEventsRequestParams = {
            program: programId,
            couponId,
            limit: store.paginations()[StoreCollection.Events].pageSize,
            page: page ?? store.paginations()[StoreCollection.Events].page,
            orderBy: "createdAt",
            order: "desc",
          };
          if (fromDate) params.fromDate = fromDate;
          if (toDate) params.toDate = toDate;

          return store._eventsService.listCouponEvents(params).pipe(
            tapResponse({
              next: events => {
                if (params.page === 1) {
                  patchState(
                    store,
                    setAllEntities(events.data ?? [], {
                      collection: StoreCollection.CouponEvents,
                      selectId: couponEvent => couponEvent._id,
                    }),
                  );
                } else {
                  patchState(
                    store,
                    setAllEntities([...(store.coupon_eventsEntities() ?? []), ...(events.data ?? [])], {
                      collection: StoreCollection.CouponEvents,
                      selectId: couponEvent => couponEvent._id,
                    }),
                  );
                }
                store.setPagination({ total: events.total, pageCount: events.pageCount }, StoreCollection.Events);
              },
              error: err => {
                Sentry.captureException(err);
                store._matDialog.open(ConfirmationComponent, {
                  data: {
                    title: "events.getEventsError",
                    errorCode: (err as any)?.error,
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),

    clearCouponEventsEntities(): void {
      patchState(
        store,
        setAllEntities([] as CouponEventDto[], { collection: StoreCollection.CouponEvents, selectId: couponEvent => couponEvent._id }),
      );
    },

    getPostEvents: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        filter(query => !!query.programId && !!query.postId),
        tap(() => store.setLoading(true)),
        switchMap(({ programId, postId, fromDate, toDate, page }) => {
          const params: ListNewsEventsRequestParams = {
            program: programId,
            newsId: postId ?? "",
            limit: store.paginations()[StoreCollection.Events].pageSize,
            page: page ?? store.paginations()[StoreCollection.Events].page,
            orderBy: "createdAt",
            order: "desc",
          };
          if (fromDate) params.fromDate = fromDate;
          if (toDate) params.toDate = toDate;

          return store._postEventsService.listNewsEvents(params).pipe(
            tapResponse({
              next: events => {
                if (params.page === 1) {
                  patchState(
                    store,
                    setAllEntities(events.data ?? [], {
                      collection: StoreCollection.PostEvents,
                      selectId: postEvent => postEvent._id,
                    }),
                  );
                } else {
                  patchState(
                    store,
                    setAllEntities([...(store.post_eventsEntities() ?? []), ...(events.data ?? [])], {
                      collection: StoreCollection.PostEvents,
                      selectId: postEvent => postEvent._id,
                    }),
                  );
                }
                store.setPagination({ total: events.total, pageCount: events.pageCount }, StoreCollection.Events);
              },
              error: err => {
                Sentry.captureException(err);
                store._matDialog.open(ConfirmationComponent, {
                  data: {
                    title: "events.getEventsError",
                    errorCode: (err as any)?.error,
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),

    clearPostEventsEntities(): void {
      patchState(
        store,
        setAllEntities([] as NewsEventDto[], {
          collection: StoreCollection.PostEvents,
          selectId: postEvent => postEvent._id,
        }),
      );
    },

    getCustomerEvents: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        filter(query => !!query.programId && (!!query.accountId || !!query.userId)),
        tap(() => store.setLoading(true)),
        switchMap(({ programId, fromDate, toDate, page }) => {
          const params: ListAccountEventsRequestParams = {
            program: programId,
            limit: store.paginations()[StoreCollection.Events].pageSize,
            page: page ?? store.paginations()[StoreCollection.Events].page,
            accountId: store.accountId() ?? undefined,
            userId: store.userId() ?? undefined,
            membershipId: store.membershipId() ?? undefined,
            orderBy: "createdAt",
            order: "desc",
          };
          if (fromDate) params.fromDate = fromDate;
          if (toDate) params.toDate = toDate;

          return store._customerEventsService.listAccountEvents(params).pipe(
            tapResponse({
              next: events => {
                if (params.page === 1) {
                  patchState(
                    store,
                    setAllEntities(events.data ?? [], {
                      collection: StoreCollection.CustomerEvents,
                      selectId: customerEvent => customerEvent._id,
                    }),
                  );
                } else {
                  patchState(
                    store,
                    setAllEntities([...(store.customer_eventsEntities() ?? []), ...(events.data ?? [])], {
                      collection: StoreCollection.CustomerEvents,
                      selectId: customerEvent => customerEvent._id,
                    }),
                  );
                }
                store.setPagination({ total: events.total, pageCount: events.pageCount }, StoreCollection.Events);
              },
              error: err => {
                Sentry.captureException(err);
                store._matDialog.open(ConfirmationComponent, {
                  data: {
                    title: "events.getEventsError",
                    errorCode: (err as any)?.error,
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),

    clearCustomerEventsEntities(): void {
      patchState(
        store,
        setAllEntities([] as CustomerEventDto[], {
          collection: StoreCollection.CustomerEvents,
          selectId: customerEvent => customerEvent._id,
        }),
      );
    },

    getWorkflowEvents: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        filter(query => !!query.programId && !!query.workflowId),
        tap(() => store.setLoading(true)),
        switchMap(({ programId, workflowId, fromDate, toDate, page }) => {
          const params: ListWorkflowEventsRequestParams = {
            program: programId,
            workflowId: workflowId ?? "",
            limit: store.paginations()[StoreCollection.Events].pageSize,
            page: page ?? store.paginations()[StoreCollection.Events].page,
            orderBy: "createdAt",
            order: "desc",
          };
          if (fromDate) params.fromDate = fromDate;
          if (toDate) params.toDate = toDate;

          return store._workflowEventsService.listWorkflowEvents(params).pipe(
            tapResponse<WorkflowEventDtoResponse>({
              next: events => {
                if (params.page === 1) {
                  patchState(
                    store,
                    setAllEntities(events.data ?? [], {
                      collection: StoreCollection.WorkflowEvents,
                      selectId: workflowEvent => workflowEvent._id,
                    }),
                  );
                } else {
                  patchState(
                    store,
                    setAllEntities([...(store.workflow_eventsEntities() ?? []), ...(events.data ?? [])], {
                      collection: StoreCollection.WorkflowEvents,
                      selectId: workflowEvent => workflowEvent._id,
                    }),
                  );
                }
                store.setPagination({ total: events.total, pageCount: events.pageCount }, StoreCollection.Events);
              },
              error: err => {
                Sentry.captureException(err);
                store._matDialog.open(ConfirmationComponent, {
                  data: {
                    title: "events.getEventsError",
                    errorCode: (err as any)?.error,
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),

    clearWorkflowEventsEntities(): void {
      patchState(
        store,
        setAllEntities([] as WorkflowEventDto[], {
          collection: StoreCollection.WorkflowEvents,
          selectId: workflowEvent => workflowEvent._id,
        }),
      );
    },

    updateFiltersAndPagination: (fromDate: string, toDate: string): void => {
      store.setPagination({ total: 0, pageCount: 0, page: 1 }, StoreCollection.Events);
      store.setDatesFilter(fromDate, toDate);
    },
  })),
);
